<script lang="ts">
  import { onDestroy } from 'svelte';
  import { resolveByQuery } from '../lib/api';
  import { Autocomplete, type AutocompleteView } from '../lib/autocomplete.svelte';
  import { parseCoord } from '../lib/debug-walk';
  import type { Endpoint, EndpointPickSource } from '../lib/endpoint';
  import { formatLatLon } from '../lib/endpoint';
  import type { Suggestion } from '../lib/recents';
  import { toast } from '../stores/toast.svelte';
  import type { ResolveResult } from '../types/ResolveResult';
  import SuggestionsList from './suggestions-list.svelte';

  interface Properties {
    label: string;
    placeholder?: string;
    /** Recents-store scope ("route", "dep", "wk"). */
    scope: string;
    /** Current text — parent controls it so URL restore can inject an initial value. */
    value: string;
    /** Called on every keystroke so the parent can mirror `value`. */
    onInput: (next: string) => void;
    /**
     * Called when the user picks a suggestion (click or Enter). `source` carries the
     * suggestion's coord (from the underlying `ResolveResult` or recent) so callers
     * that want to drop a pin at that point have the lat/lon.
     */
    onPick: (ep: Endpoint, label: string, source?: EndpointPickSource) => void;
    /** Optional CSS class for the input — used by the parent to colour from/to. */
    inputClass?: string;
    /** Freezes typing + dropdown while the parent is running a search. */
    disabled?: boolean;
    /**
     * When true, typing a valid `"lat,lon"` string surfaces a POSITION suggestion
     * at the top of the dropdown that picks as `{ kind: 'position', ... }`.
     * Defaults to false so the main-page inputs stay address/stop-only.
     */
    allowPosition?: boolean;
    /**
     * When set, renders a "Pick from map" button next to the input. Clicking it
     * arms the parent's map-click picker; the parent is responsible for actually
     * calling `onPick` once the user clicks the map.
     */
    onPickFromMap?: () => void;
    /** True when this input's picker is currently armed — visual cue on the button. */
    pickerArmed?: boolean;
  }

  const {
    label,
    placeholder = 'Search stops or addresses',
    scope,
    value,
    onInput,
    onPick,
    inputClass = '',
    disabled = false,
    allowPosition = false,
    onPickFromMap,
    pickerArmed = false,
  }: Properties = $props();

  function toView(result: ResolveResult): AutocompleteView<Endpoint> {
    const coord = `${String(result.lat)}:${String(result.lon)}`;
    if (result.stop_id != undefined) {
      return {
        key: `stop:${result.name}:${coord}`,
        label: result.name,
        badge: 'STOP',
        pick: { kind: 'stop', stopId: result.stop_id },
        recent: { name: result.name, lat: result.lat, lon: result.lon, stop_id: result.stop_id },
      };
    }
    return {
      key: `addr:${result.name}:${coord}`,
      label: result.name,
      badge: 'ADDR',
      pick: { kind: 'address', address: result.name },
      recent: { name: result.name, lat: result.lat, lon: result.lon },
    };
  }

  function recentToView(suggestion: Suggestion): AutocompleteView<Endpoint> {
    const coord = `${String(suggestion.lat)}:${String(suggestion.lon)}`;
    if (suggestion.kind === 'position') {
      return {
        key: `recent:pos:${coord}`,
        label: suggestion.name,
        badge: 'RECENT',
        pick: { kind: 'position', latitude: suggestion.lat, longitude: suggestion.lon },
        recent: suggestion,
      };
    }
    if (suggestion.stop_id != undefined) {
      return {
        key: `recent:stop:${suggestion.name}:${coord}`,
        label: suggestion.name,
        badge: 'RECENT',
        pick: { kind: 'stop', stopId: suggestion.stop_id },
        recent: suggestion,
      };
    }
    return {
      key: `recent:addr:${suggestion.name}:${coord}`,
      label: suggestion.name,
      badge: 'RECENT',
      pick: { kind: 'address', address: suggestion.name },
      recent: suggestion,
    };
  }

  /** Synthesises a POSITION dropdown row from a `"lat,lon"` query when `allowPosition` is on. */
  function positionSuggestion(query: string): AutocompleteView<Endpoint> | undefined {
    if (!allowPosition) return undefined;
    const coord = parseCoord(query);
    if (!coord) return undefined;
    const [latitude, longitude] = coord;
    const label = formatLatLon(latitude, longitude);
    return {
      key: `pos:${String(latitude)}:${String(longitude)}`,
      label,
      badge: 'POSITION',
      pick: { kind: 'position', latitude, longitude },
      recent: { name: label, lat: latitude, lon: longitude, kind: 'position' },
    };
  }

  const autocomplete = new Autocomplete<ResolveResult, Endpoint>({
    scope: () => scope,
    fetcher: async (query, signal) => {
      const response = await resolveByQuery(query, signal);
      return response.results;
    },
    toView,
    recentToView,
    toast,
    toastPrefix: 'Address lookup',
    synthesizePick: (query) => positionSuggestion(query),
  });

  function handleInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    onInput(target.value);
    autocomplete.handleInput(target.value);
  }

  /** Extracts the source coord from a picked suggestion's `recent`. */
  function sourceFromRecent(recent: Suggestion): EndpointPickSource {
    return { latitude: recent.lat, longitude: recent.lon };
  }

  function onKeydown(event: KeyboardEvent): void {
    const picked = autocomplete.onKeydown(event);
    if (picked) {
      onInput(picked.label);
      onPick(picked.pick, picked.label, sourceFromRecent(picked.recent));
    }
  }

  function pick(index: number): void {
    const picked = autocomplete.pick(index);
    onInput(picked.label);
    onPick(picked.pick, picked.label, sourceFromRecent(picked.recent));
  }

  onDestroy(() => {
    autocomplete.dispose();
  });

  const inputId = $derived(`ep-${scope}-${label}`);
  const inputClasses = $derived(`io-input ${inputClass}`);
</script>

<!-- Autocomplete-first origin/dest input. Debounces the fetch, aborts the
     in-flight request on the next keystroke, merges recents with results,
     and calls `onPick` with a typed Endpoint when a suggestion is chosen. -->
<div class="endpoint-wrap">
  <label class="endpoint-label" for={inputId}>{label}</label>
  <div class="endpoint-body">
    <div class="endpoint-row">
      <input
        id={inputId}
        type="text"
        class={inputClasses}
        {placeholder}
        {value}
        {disabled}
        autocomplete="off"
        oninput={handleInput}
        onfocus={() => {
          autocomplete.onFocus(value);
        }}
        onblur={() => {
          autocomplete.onBlur();
        }}
        onkeydown={onKeydown}
      />
      {#if onPickFromMap}
        <button
          type="button"
          class="pick-btn"
          class:pick-armed={pickerArmed}
          aria-pressed={pickerArmed ? 'true' : 'false'}
          aria-label={`Pick ${label} from map`}
          {disabled}
          onclick={onPickFromMap}
        >
          Pick on map
        </button>
      {/if}
    </div>
    {#if autocomplete.open}
      <SuggestionsList
        items={autocomplete.items}
        activeIndex={autocomplete.activeIndex}
        emptyLabel="Start typing to search…"
        onSelect={pick}
        onHover={(index: number) => {
          autocomplete.setActive(index);
        }}
      />
    {/if}
  </div>
</div>

<style>
  .endpoint-wrap {
    margin-bottom: var(--space-sm);
  }

  .endpoint-label {
    display: block;
    font-size: var(--font-size-xs);
    color: var(--color-fg-muted);
    margin-bottom: 2px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .endpoint-body {
    position: relative;
  }

  .endpoint-row {
    display: flex;
    gap: var(--space-xs);
    align-items: stretch;
  }

  .endpoint-row input {
    flex: 1;
    min-width: 0;
  }

  .pick-btn {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    color: var(--color-primary);
    padding: 4px 10px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    font-size: var(--font-size-sm);
    white-space: nowrap;
  }

  .pick-btn:hover:not(:disabled) {
    border-color: var(--color-primary);
    background: var(--color-surface-alt);
  }

  .pick-armed {
    background: var(--color-primary);
    color: var(--color-bg);
    border-color: var(--color-primary);
  }
</style>
